import API_BASE from '../config';
import React, { useEffect, useRef, useState } from 'react';

export type AresSuggestion = {
  ico: string;
  companyName: string;
  dic: string;
  address: {
    country: string;
    region: string;
    city: string;
    zip: string;
    street: string;
  };
  textAddress?: string;
};

const DISMISS_EVENT = 'aresSuggestDismiss';

type Props = {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: AresSuggestion) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Hide built-in label (e.g. when wrapped in FormRow) */
  hideLabel?: boolean;
  inputClassName?: string;
};

const AresSuggestInput: React.FC<Props> = ({
  id,
  label,
  value,
  onChange,
  onSelect,
  placeholder,
  disabled,
  hideLabel = false,
  inputClassName = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50',
}) => {
  const [suggestions, setSuggestions] = useState<AresSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Only search after the user types in this field (not on mount / tab return)
  const allowSearch = useRef(false);

  const dismiss = () => {
    setSuggestions([]);
    setOpen(false);
    setLoading(false);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onDismissAll = () => {
      allowSearch.current = false;
      dismiss();
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener(DISMISS_EVENT, onDismissAll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener(DISMISS_EVENT, onDismissAll);
    };
  }, []);

  useEffect(() => {
    if (!allowSearch.current) {
      dismiss();
      return;
    }
    const q = value.trim();
    if (q.length < 2) {
      dismiss();
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/ares/suggest?q=${encodeURIComponent(q)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          dismiss();
          return;
        }
        const list = data.suggestions || [];
        setSuggestions(list);
        setOpen(list.length > 0);
      } catch {
        if (!cancelled) dismiss();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 320);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value]);

  const handleSelect = (s: AresSuggestion) => {
    allowSearch.current = false;
    dismiss();
    window.dispatchEvent(new Event(DISMISS_EVENT));
    onSelect(s);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      {!hideLabel && (
        <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        autoComplete="off"
        onChange={e => {
          allowSearch.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        placeholder={placeholder}
        className={inputClassName}
      />
      {loading && (
        <p
          className={`absolute right-3 text-[11px] text-gray-400 ${
            hideLabel ? 'top-2' : 'top-9'
          }`}
        >
          ARES…
        </p>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {suggestions.map(s => (
            <li key={`${s.ico}-${s.companyName}`}>
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(s)}
                className="w-full text-left px-3 py-2.5 hover:bg-primary-50 transition-colors border-b border-gray-50 last:border-0"
              >
                <div className="text-sm font-medium text-gray-900 truncate">{s.companyName}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  IČO {s.ico}
                  {s.dic ? ` · ${s.dic}` : ''}
                  {s.textAddress ? ` · ${s.textAddress}` : ''}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AresSuggestInput;
